/**
 * 筹码数字滚动（§11.6：600ms 文本插值）。
 *
 * 红线 #48：筹码滚动**不走 React state**。每帧 setState 会把整个座位块甚至整张牌桌
 * 重新 diff 一遍，600ms × 60fps = 36 次渲染，中端机上直接吃掉一帧预算。
 * 这里直接写 `textContent`，React 只负责挂载那个 `<span>`。
 * 宽度稳定靠 `.num`（tabular-nums），否则数字滚动时整行会左右抖。
 */

import { useEffect, useRef } from 'react'

import { MOTION } from './motion.js'

/** easeOutCubic：起步快、收尾稳，数字滚动用它比线性更像「结算」而不是「计数器」 */
export function easeOutCubic(t: number): number {
  const c = 1 - Math.min(1, Math.max(0, t))
  return 1 - c * c * c
}

/** 插值到第 t（0..1）时应显示的整数值。筹码全是整数（红线 #17），必须 round。 */
export function countAt(from: number, to: number, t: number): number {
  if (t <= 0) return Math.round(from)
  if (t >= 1) return Math.round(to)
  return Math.round(from + (to - from) * easeOutCubic(t))
}

export interface RollOptions {
  readonly durationMs?: number
  readonly reduced?: boolean
  readonly format?: (n: number) => string
}

/**
 * 把 `value` 的变化渲染成滚动数字。返回值挂到 `<span ref={ref} className="num" />`。
 * 首帧不滚动（直接落位），否则每次进牌桌所有筹码都从 0 爬上来，像在读秒。
 */
export function useRollingNumber(value: number, o: RollOptions = {}): (el: HTMLElement | null) => void {
  const elRef = useRef<HTMLElement | null>(null)
  const fromRef = useRef<number>(value)
  const frameRef = useRef<number>(0)
  const format = o.format ?? ((n: number) => String(n))
  const formatRef = useRef(format)
  formatRef.current = format
  const duration = o.durationMs ?? MOTION.count.duration
  const reduced = o.reduced ?? false

  useEffect(() => {
    const el = elRef.current
    const from = fromRef.current
    fromRef.current = value
    if (el === null) return undefined
    if (reduced || duration <= 0 || from === value) {
      el.textContent = formatRef.current(value)
      return undefined
    }
    const start = performance.now()
    const step = (now: number): void => {
      const t = (now - start) / duration
      el.textContent = formatRef.current(countAt(from, value, t))
      if (t < 1) frameRef.current = requestAnimationFrame(step)
      else frameRef.current = 0
    }
    if (frameRef.current !== 0) cancelAnimationFrame(frameRef.current)
    frameRef.current = requestAnimationFrame(step)
    return () => {
      if (frameRef.current !== 0) {
        cancelAnimationFrame(frameRef.current)
        frameRef.current = 0
      }
    }
  }, [value, duration, reduced])

  return (el: HTMLElement | null): void => {
    elRef.current = el
    // 挂载瞬间先落位，避免 effect 跑之前显示空白
    if (el !== null && el.textContent === '') el.textContent = formatRef.current(value)
  }
}
